import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Input, Button } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { EventJob } from '@/api/media/live-event-jobs-types';
import { LMS_INGEST_HOST, withLmsIngestHost, toLmsOutputUrl } from '../../lms-endpoint-format';
import { FormSectionCard } from './FormSectionCard';
import styles from './EventJobCreate.module.css';

interface CopyInputProps {
  label: string;
  value: string;
}

function CopyInput({ label, value }: CopyInputProps) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Field label={label}>
      {(fp) => (
        <div className={styles.copyRow}>
          <Input {...fp} value={value} readOnly />
          <Button variant="ghost" size="sm" onClick={copy} aria-label="Copy">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </Button>
        </div>
      )}
    </Field>
  );
}

interface ResultSectionProps {
  job: EventJob;
  title?: string;
}

const PRESET_LABEL_MAP: Record<string, string> = {
  manifest: 'Manifest',
  transmux: 'Transmux',
  audio_only: 'Audio Only',
};

/** Extract path-only playback entries from provider_metadata.streams[0].outbound. */
function extractPlaybackEntries(job: EventJob): { label: string; url: string }[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = job as any;
  const providerStream = raw.provider_metadata?.streams?.[0];
  const outbound: Record<string, Record<string, string> | null> = providerStream?.outbound ?? {};

  const entries: { label: string; url: string }[] = [];
  for (const [outputType, paths] of Object.entries(outbound)) {
    if (!paths) continue;
    for (const [presetKey, path] of Object.entries(paths)) {
      // Output URL: https://<DELIVERY-DOMAIN> + path (provider host dropped).
      const url = toLmsOutputUrl(path as string);
      if (!url) continue;
      const presetLabel = PRESET_LABEL_MAP[presetKey] ?? presetKey;
      entries.push({ label: `${outputType.toUpperCase()} — ${presetLabel}`, url });
    }
  }

  // Fallback: job.outbound top-level map when provider_metadata not present
  if (entries.length === 0 && raw.outbound) {
    for (const [proto, variants] of Object.entries(raw.outbound as Record<string, Record<string, string>>)) {
      for (const [variant, url] of Object.entries(variants)) {
        if (typeof url === 'string' && url) {
          entries.push({ label: `${proto.toUpperCase().replace('_', '-')} / ${variant}`, url: toLmsOutputUrl(url) });
        }
      }
    }
  }

  return entries;
}

/** Success state — shows ingest push URL and all outbound playback URLs. */
export function ResultSection({ job, title = 'Event Job Created' }: ResultSectionProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = job as any;
  const providerStream = raw.provider_metadata?.streams?.[0];

  // Push URL: prefer provider stream publish.push_url, fallback to top-level, then
  // advertise it on the fixed LMS ingest host (scheme + path preserved).
  const rawPushUrl: string = providerStream?.publish?.push_url || job.push_url || '';
  const pushUrl = rawPushUrl ? withLmsIngestHost(rawPushUrl) : '';

  const playbackEntries = extractPlaybackEntries(job);

  return (
    <FormSectionCard
      title={title}
      description="Copy the ingest URL to your encoder and share the playback URLs with viewers."
    >
      {pushUrl && <CopyInput label="Ingest (Push) URL" value={pushUrl} />}
      <CopyInput label="Ingestion Domain" value={LMS_INGEST_HOST} />

      {playbackEntries.length > 0 && (
        <>
          <p className={styles.subLabel}>Playback URLs</p>
          {playbackEntries.map(({ label, url }) => (
            <CopyInput key={label} label={label} value={url} />
          ))}
        </>
      )}
    </FormSectionCard>
  );
}
